"use client"

import { useState, useEffect } from "react"
import { X, Loader2, Folder, Plus, Trash2, Edit, Check, Upload, Building2 } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from "@mantis-sas/ui";
import { getCategories, createCategory, updateCategory, deleteCategory, getCostCenters, createCostCenter, updateCostCenter, deleteCostCenter } from "../../../../ApiCalls/mantisFlowsCategories";
import BulkUploadModal from "../../../components/BulkUploadModal"
import { useBulkUpload, categoriesBulkConfig, costCentersBulkConfig } from "../../../configuration/bulk"

export default function CategoriesCrud({ 
  clientId, 
  isCategoryModalOpen, 
  setIsCategoryModalOpen, 
  isSubcategoryModalOpen, 
  setIsSubcategoryModalOpen, 
  searchTerm,
  defaultTab = "categories",
  embedded = false 
}) {
  const [categories, setCategories] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [newCategory, setNewCategory] = useState("");
  const [newCostCenter, setNewCostCenter] = useState({
    name: "",
    categoryId: "",
  });
  const [loading, setLoading] = useState(false);
  const [newSubcategory, setNewSubcategory] = useState({
    name: "",
    categoryId: "",
  });
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingCostCenter, setEditingCostCenter] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState({
    id: null,
    name: "",
    categoryId: "",
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    type: "",
    id: "",
    name: "",
    hasSubcategories: false,
    subcategoryCount: 0,
  });
  const [deleteCostCenterDialog, setDeleteCostCenterDialog] = useState({
    open: false,
    id: "",
    name: ""
  });
  const [, setSubmitting] = useState(false);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [isCostCenterBulkUploadOpen, setIsCostCenterBulkUploadOpen] =
    useState(false);
  const [isCostCenterModalOpen, setIsCostCenterModalOpen] = useState(false);

  // Internal modal state management for embedded mode
  const [internalCategoryModalOpen, setInternalCategoryModalOpen] =
    useState(false);

  // Establecer valores por defecto para props que pueden no estar definidos
  const safeCategoryModalOpen =
    isCategoryModalOpen !== undefined
      ? isCategoryModalOpen
      : internalCategoryModalOpen;
  const safeSetCategoryModalOpen =
    setIsCategoryModalOpen || setInternalCategoryModalOpen;

  // Unified modal state for both category and subcategory creation
  const [modalMode, setModalMode] = useState("category"); // 'category' or 'subcategory'

  // Function to open modal in specific mode
  const openModal = (mode, parentCategoryId = null) => {
    setModalMode(mode);
    if (mode === "subcategory") {
      // Reset subcategory form when opening subcategory mode
      setNewSubcategory({
        name: "",
        categoryId: parentCategoryId || ""
      });
    } else {
      // Reset category form when opening category mode
      setNewCategory("");
      // Also reset subcategory form when switching to category mode
      setNewSubcategory({name: "", categoryId: ""});
    }
    safeSetCategoryModalOpen(true);
  };

  const safeSearchTerm = searchTerm || "";

  const {toast} = useToast();
  const {isUploading, uploadResult, handleBulkUpload} = useBulkUpload(
    clientId,
    categoriesBulkConfig
  );
  const {
    isUploading: isCostCenterUploading,
    uploadResult: costCenterUploadResult,
    handleBulkUpload: handleCostCenterBulkUpload
  } = useBulkUpload(clientId, costCentersBulkConfig);

  useEffect(() => {
    fetchCategories();
    fetchCostCenters();

    // Add event listeners for loading state
    window.addEventListener("categoriesLoading", handleLoadingEvent);
    window.addEventListener("categorySaving", handleSavingEvent);
    window.addEventListener("categoryUpdating", handleSavingEvent);
    window.addEventListener("categoryDeleting", handleSavingEvent);
    window.addEventListener("costCentersLoading", handleLoadingEvent);
    window.addEventListener("costCenterSaving", handleSavingEvent);
    window.addEventListener("costCenterUpdating", handleSavingEvent);
    window.addEventListener("costCenterDeleting", handleSavingEvent);

    return () => {
      window.removeEventListener("categoriesLoading", handleLoadingEvent);
      window.removeEventListener("categorySaving", handleSavingEvent);
      window.removeEventListener("categoryUpdating", handleSavingEvent);
      window.removeEventListener("categoryDeleting", handleSavingEvent);
      window.removeEventListener("costCentersLoading", handleLoadingEvent);
      window.removeEventListener("costCenterSaving", handleSavingEvent);
      window.removeEventListener("costCenterUpdating", handleSavingEvent);
      window.removeEventListener("costCenterDeleting", handleSavingEvent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle loading state event
  const handleLoadingEvent = (event) => {
    setLoading(event.detail);
  };

  // Handle saving state event
  const handleSavingEvent = (event) => {
    setSubmitting(event.detail);
  };

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await getCategories(clientId);
      if (res.success) {
        setCategories(res.data);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCostCenters = async () => {
    setLoading(true);
    try {
      const res = await getCostCenters(clientId);
      if (res.success) setCostCenters(res.data);
    } catch (error) {
      console.error("Error fetching cost centers:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;
    setLoading(true);
    try {
      await createCategory(clientId, newCategory.trim());
      setNewCategory("");
      fetchCategories();

      toast({
        title: "Éxito",
        description: "Categoría creada correctamente",
        variant: "default"
      });
    } catch (error) {
      console.error("Error creating category:", error);
      toast({
        title: "Error",
        description: "Error al crear categoría",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddCostCenter = async () => {
    if (!newCostCenter.name.trim()) return;
    setLoading(true);
    try {
      await createCostCenter(
        clientId,
        newCostCenter.name.trim(),
        newCostCenter.categoryId || null
      );
      setNewCostCenter({name: "", categoryId: ""});
      fetchCostCenters();
      toast({
        title: "Success",
        description: "Cost center created successfully",
        variant: "default"
      });
    } catch (error) {
      toast({
        title: "Error",
        description:
          error.response?.data?.message || "Failed to create cost center",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubcategory = async () => {
    if (!newSubcategory.name.trim() || !newSubcategory.categoryId) return;
    setLoading(true);
    try {
      // Use createCategory API with parentId to create a subcategory
      await createCategory(
        clientId,
        newSubcategory.name.trim(),
        newSubcategory.categoryId, // parentId
        "" // description (empty string)
      );

      // Reset form and refresh categories
      setNewSubcategory({name: "", categoryId: ""});
      fetchCategories();

      toast({
        title: "Éxito",
        description: "Subcategoría creada correctamente",
        variant: "default"
      });
    } catch (error) {
      console.error("Error creating subcategory:", error);
      toast({
        title: "Error",
        description:
          error.response?.data?.message || "Error al crear subcategoría",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  
  const handleUpdateCostCenter = async () => {
    if (!editingCostCenter || !editingCostCenter.name.trim()) {
      toast({
        title: "Error",
        description: "Cost center name cannot be empty",
        variant: "destructive"
      });
      return;
    }

    try {
      setSubmitting(true);
      const response = await updateCostCenter(clientId, editingCostCenter.id, {
        name: editingCostCenter.name.trim(),
        category_id: editingCostCenter.category_id || null
      });

      if (response.success) {
        toast({
          title: "Success",
          description: "Cost center updated successfully",
          variant: "default"
        });
        setEditingCostCenter(null);
        fetchCostCenters();
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to update cost center",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error updating cost center:", error);
      toast({
        title: "Error",
        description:
          error.response?.data?.message || "Failed to update cost center",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    setLoading(true);
    try {
      await deleteCategory(clientId, categoryId);
      fetchCategories();

      toast({
        title: "Éxito",
        description: "Categoría eliminada correctamente",
        variant: "default"
      });
    } catch (error) {
      console.error("Error deleting category:", error);
      toast({
        title: "Error",
        description: "Error al eliminar categoría",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCostCenter = async (costCenterId) => {
    setLoading(true);
    try {
      await deleteCostCenter(clientId, costCenterId);
      fetchCostCenters();
      toast({
        title: "Success",
        description: "Cost center deleted successfully",
        variant: "default"
      });
    } catch (error) {
      toast({
        title: "Error",
        description:
          error.response?.data?.message || "Failed to delete cost center",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  
  const handleEditCategory = async () => {
    if (!editingCategory.name.trim()) return;
    setLoading(true);
    try {
      await updateCategory(clientId, editingCategory.id, {
        name: editingCategory.name.trim()
      });
      setEditingCategory(null);
      fetchCategories();

      toast({
        title: "Éxito",
        description: editingCategory.isSubcategory
          ? "Subcategoría actualizada correctamente"
          : "Categoría actualizada correctamente",
        variant: "default"
      });
    } catch (error) {
      console.error("Error updating category:", error);
      toast({
        title: "Error",
        description: editingCategory.isSubcategory
          ? "Error al actualizar subcategoría"
          : "Error al actualizar categoría",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  
  // Process categories to create a flat list with hierarchy information
  const processCategories = (categoriesData) => {
    if (!categoriesData || !Array.isArray(categoriesData)) return [];

    // Create a map of all categories by ID for quick lookup
    const categoriesMap = {};
    categoriesData.forEach((cat) => {
      categoriesMap[cat.id] = cat;
    });

    // Function to get the full path of a category (including all parent names)
    const getCategoryPath = (category) => {
      const path = [category.name];
      let current = category;

      while (current.parent_id && categoriesMap[current.parent_id]) {
        current = categoriesMap[current.parent_id];
        path.unshift(current.name);
      }

      return path;
    };

    // Function to calculate the depth level of a category
    const getCategoryDepth = (category) => {
      let depth = 0;
      let current = category;

      while (current.parent_id && categoriesMap[current.parent_id]) {
        depth++;
        current = categoriesMap[current.parent_id];
      }

      return depth;
    };

    // Process all categories to add hierarchy information
    const processedCategories = categoriesData.map((cat) => {
      const depth = getCategoryDepth(cat);
      const path = getCategoryPath(cat);
      const parentName =
        cat.parent_id && categoriesMap[cat.parent_id]
          ? categoriesMap[cat.parent_id].name
          : null;

      return {
        ...cat,
        depth,
        path,
        parentName,
        isSubcategory: !!cat.parent_id
      };
    });

    // Sort by depth (main categories first) then by name
    processedCategories.sort((a, b) => {
      if (a.depth !== b.depth) {
        return a.depth - b.depth;
      }
      return a.name.localeCompare(b.name);
    });

    return processedCategories;
  };

  // Filtrado global para categorías y subcategorías
  const processedCategories = processCategories(categories);
  const filteredCategories = (processedCategories || []).filter((cat) => {
    if (!safeSearchTerm) return true;

    // Check if the category name matches the search term
    if (cat.name.toLowerCase().includes(safeSearchTerm.toLowerCase())) {
      return true;
    }

    // Check if any parent in the path matches the search term
    if (
      cat.path &&
      cat.path.some((pathPart) =>
        pathPart.toLowerCase().includes(safeSearchTerm.toLowerCase())
      )
    ) {
      return true;
    }

    return false;
  });

  // Filtrado para centros de costos
  const filteredCostCenters = (costCenters || []).filter((center) => {
    if (!safeSearchTerm) return true;

    const categoryName = center.category_id
      ? categories.find((cat) => cat.id === center.category_id)?.name
      : null;

    return (
      center.name.toLowerCase().includes(safeSearchTerm.toLowerCase()) ||
      (categoryName &&
        categoryName.toLowerCase().includes(safeSearchTerm.toLowerCase()))
    );
  });

  return (
    <div className="space-y-6">
      <Tabs
        defaultValue={defaultTab}
        value={activeTab}
        onValueChange={setActiveTab}
        className="w-full"
      >
        <div className="flex justify-between items-center mb-6">
          {!embedded ? (
            <TabsList className="flex gap-2 bg-slate-100 dark:bg-slate-800 rounded p-2">
              <TabsTrigger
                value="categories"
                className="data-[state=active]:bg-orange-500 data-[state=active]:text-white px-6 py-2 rounded font-semibold"
              >
                <Folder className="h-4 w-4 mr-2" />
                Categorías
              </TabsTrigger>
              <TabsTrigger
                value="costcenters"
                className="data-[state=active]:bg-orange-500 data-[state=active]:text-white px-6 py-2 rounded font-semibold"
              >
                <Building2 className="h-4 w-4 mr-2" />
                Centros de Costos
              </TabsTrigger>
            </TabsList>
          ) : (
            <div className="flex items-center gap-3">
              {activeTab === "categories" ? <></> : <></>}
            </div>
          )}

          <div className="flex gap-2">
            {activeTab === "categories" && (
              <>
                {/*<Button
                  onClick={() => setIsBulkUploadOpen(true)}
                  variant="outline"
                  className="border-orange-500 text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Carga Masiva Categorías
                </Button>*/}
                <Button
                  onClick={() => openModal("category")}
                  className="bg-orange-500 hover:bg-orange-600 text-white mt-2 mr-2"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Nueva Categoría
                </Button>
                <Button
                  onClick={() => openModal("subcategory")}
                  className="bg-blue-500 hover:bg-blue-600 text-white mt-2 mr-2"
                  disabled={categories.length === 0}
                  title={
                    categories.length === 0
                      ? "Primero debes crear una categoría"
                      : ""
                  }
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Nueva Subcategoría
                  {categories.length === 0 && (
                    <span className="ml-1 text-xs opacity-75">
                      (requiere categoría)
                    </span>
                  )}
                </Button>
              </>
            )}
            {activeTab === "costcenters" && (
              <div className="flex gap-2 mt-2 mr-2">
                <Button
                  onClick={() => setIsCostCenterBulkUploadOpen(true)}
                  variant="outline"
                  className="border-orange-500 text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Carga Masiva Centros de Costos
                </Button>
                <Button
                  onClick={() => setIsCostCenterModalOpen(true)}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Nuevo Centro de Costos
                </Button>
              </div>
            )}
          </div>
        </div>

        <TabsContent value="categories">
          <div className="overflow-hidden">
            <div>
              {loading ? (
                <div className="p-8 text-center text-slate-500">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                  <p>Cargando categorías...</p>
                </div>
              ) : filteredCategories.length === 0 ? (
                <div className="p-8 text-center text-slate-500 flex flex-col items-center justify-center min-h-[200px]">
                  <Folder className="h-12 w-12 mx-auto mb-3 text-slate-400" />
                  <p className="text-lg text-center">
                    No se encontraron categorías
                  </p>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                  <thead className="bg-slate-50 dark:bg-slate-800/80">
                    <tr>
                      <th className="px-6 py-2 text-left font-semibold text-slate-700 dark:text-slate-200">
                        Nombre
                      </th>
                      <th className="px-6 py-2 text-left font-semibold text-slate-700 dark:text-slate-200">
                        Padre
                      </th>
                      <th className="px-6 py-2 text-center font-semibold text-slate-700 dark:text-slate-200">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                    {filteredCategories.map((cat) => (
                      <tr
                        key={cat.id}
                        className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                      >
                        <td className="px-6 py-2 align-top">
                          {editingCategory && editingCategory.id === cat.id ? (
                            <div className="flex items-center gap-2">
                              <Input
                                value={editingCategory.name}
                                onChange={(e) =>
                                  setEditingCategory({
                                    ...editingCategory,
                                    name: e.target.value
                                  })
                                }
                                className="w-48"
                              />
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={handleEditCategory}
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => setEditingCategory(null)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <div
                                className="flex items-center gap-2"
                                style={{marginLeft: `${cat.depth * 24}px`}}
                              >
                                {cat.isSubcategory ? (
                                  <Folder className="h-4 w-4 text-blue-500" />
                                ) : (
                                  <Folder className="h-4 w-4 text-orange-500" />
                                )}
                                <span className="font-medium text-slate-900 dark:text-slate-100">
                                  {cat.name}
                                </span>
                                {cat.isSubcategory && (
                                  <span className="text-xs text-slate-500 dark:text-slate-400">
                                    ({cat.path.join(" > ")})
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-2 align-top">
                          {cat.parentName ? (
                            <span className="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-full px-3 py-1 text-sm">
                              {cat.parentName}
                            </span>
                          ) : (
                            <span className="text-slate-400 italic text-sm">
                              Categoría principal
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-2 align-top text-center">
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 px-2 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
                              onClick={() => openModal("subcategory", cat.id)}
                              title="Agregar subcategoría"
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Sub
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="hover:bg-slate-200 dark:hover:bg-slate-700"
                              onClick={() =>
                                setEditingCategory({
                                  id: cat.id,
                                  name: cat.name,
                                  isSubcategory: cat.isSubcategory
                                })
                              }
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400"
                              onClick={() => {
                                const hasSubcategories =
                                  processedCategories.some(
                                    (subcat) => subcat.parent_id === cat.id
                                  );
                                const subcategoryCount =
                                  processedCategories.filter(
                                    (subcat) => subcat.parent_id === cat.id
                                  ).length;
                                setDeleteDialog({
                                  open: true,
                                  type: "category",
                                  id: cat.id,
                                  name: cat.name,
                                  hasSubcategories,
                                  subcategoryCount
                                });
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="costcenters">
          <div className="overflow-hidden">
            <div>
              {loading ? (
                <div className="p-8 text-center text-slate-500">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                  <p>Cargando centros de costos...</p>
                </div>
              ) : filteredCostCenters.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <Building2 className="h-12 w-12 mx-auto mb-3 text-slate-400" />
                  <p className="text-lg text-center">
                    No se encontraron centros de costos
                  </p>
                  <p className="text-sm text-center">
                    Crea tu primer centro de costos para comenzar
                  </p>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                  <thead className="bg-slate-50 dark:bg-slate-800/80">
                    <tr>
                      <th className="px-6 py-2 text-left font-semibold text-slate-700 dark:text-slate-200">
                        Nombre del Centro de Costos
                      </th>
                      <th className="px-6 py-2 text-left font-semibold text-slate-700 dark:text-slate-200">
                        Categoría
                      </th>
                      <th className="px-6 py-2 text-center font-semibold text-slate-700 dark:text-slate-200">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                    {filteredCostCenters.map((center) => (
                      <tr
                        key={center.id}
                        className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                      >
                        <td className="px-6 py-2 align-top">
                          {editingCostCenter &&
                          editingCostCenter.id === center.id ? (
                            <div className="flex items-center gap-2">
                              <Input
                                value={editingCostCenter.name}
                                onChange={(e) =>
                                  setEditingCostCenter({
                                    ...editingCostCenter,
                                    name: e.target.value
                                  })
                                }
                                className="w-48"
                              />
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-orange-500" />
                              <span className="font-medium text-slate-900 dark:text-slate-100">
                                {center.name}
                              </span>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-2 align-top">
                          {editingCostCenter &&
                          editingCostCenter.id === center.id ? (
                            <div className="flex items-center gap-2">
                              <select
                                value={editingCostCenter.category_id || ""}
                                onChange={(e) =>
                                  setEditingCostCenter({
                                    ...editingCostCenter,
                                    category_id: e.target.value || null
                                  })
                                }
                                className="w-48 p-2 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm"
                              >
                                <option value="">Sin categoría</option>
                                {categories.map((cat) => (
                                  <option key={cat.id} value={cat.id}>
                                    {cat.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          ) : (
                            <span className="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-full px-3 py-1 text-sm">
                              {center.category_id
                                ? categories.find(
                                    (cat) => cat.id === center.category_id
                                  )?.name || "Categoría no encontrada"
                                : "Sin categoría"}
                            </span>
                          )}
                        </td>

                        <td className="px-6 py-2 align-top text-center">
                          <div className="flex items-center justify-center gap-2">
                            {editingCostCenter &&
                            editingCostCenter.id === center.id ? (
                              <>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="hover:bg-green-100 dark:hover:bg-green-900/30 text-green-600 dark:text-green-400"
                                  onClick={handleUpdateCostCenter}
                                  title="Confirmar cambios"
                                >
                                  <Check className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="hover:bg-slate-200 dark:hover:bg-slate-700"
                                  onClick={() => setEditingCostCenter(null)}
                                  title="Cancelar edición"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="hover:bg-slate-200 dark:hover:bg-slate-700"
                                  onClick={() =>
                                    setEditingCostCenter({
                                      id: center.id,
                                      name: center.name,
                                      category_id: center.category_id
                                    })
                                  }
                                  title="Editar centro de costos"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400"
                                  onClick={() =>
                                    setDeleteCostCenterDialog({
                                      open: true,
                                      id: center.id,
                                      name: center.name
                                    })
                                  }
                                  title="Eliminar centro de costos"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Modal Unificado para Categorías y Subcategorías */}
      <Dialog
        open={safeCategoryModalOpen}
        onOpenChange={() => {
          safeSetCategoryModalOpen(false);
          // Reset forms when modal is closed
          setNewCategory("");
          setNewSubcategory({name: "", categoryId: ""});
        }}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">
              {modalMode === "category"
                ? "Nueva Categoría"
                : "Nueva Subcategoría"}
            </DialogTitle>
          </DialogHeader>

          {/* Tabs para cambiar entre modo categoría y subcategoría */}
          <div className="flex space-x-1 mb-4">
            <button
              onClick={() => setModalMode("category")}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                modalMode === "category"
                  ? "bg-orange-500 text-white"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
              }`}
            >
              Categoría
            </button>
            <button
              onClick={() => setModalMode("subcategory")}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                modalMode === "subcategory"
                  ? "bg-orange-500 text-white"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
              }`}
            >
              Subcategoría
            </button>
          </div>

          <div className="space-y-4 py-4">
            {modalMode === "category" ? (
              // Formulario para nueva categoría
              <div className="space-y-2">
                <Label htmlFor="category-name">Nombre de la Categoría</Label>
                <Input
                  id="category-name"
                  placeholder="Ingresa el nombre de la categoría"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  disabled={loading}
                  className="w-full"
                />
              </div>
            ) : (
              // Formulario para nueva subcategoría
              <>
                <div className="space-y-2">
                  <Label htmlFor="category-select">
                    Seleccionar Categoría Padre
                  </Label>
                  <select
                    id="category-select"
                    value={newSubcategory.categoryId}
                    onChange={(e) =>
                      setNewSubcategory({
                        ...newSubcategory,
                        categoryId: e.target.value
                      })
                    }
                    className="w-full p-2 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
                    disabled={loading}
                  >
                    <option value="">Sin categoría (Opcional)</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subcategory-name">
                    Nombre de la Subcategoría
                  </Label>
                  <Input
                    id="subcategory-name"
                    placeholder="Ingresa el nombre de la subcategoría"
                    value={newSubcategory.name}
                    onChange={(e) =>
                      setNewSubcategory({
                        ...newSubcategory,
                        name: e.target.value
                      })
                    }
                    disabled={loading || !newSubcategory.categoryId}
                    className="w-full"
                  />
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button
              onClick={async () => {
                if (modalMode === "category") {
                  await handleAddCategory();
                } else {
                  await handleAddSubcategory();
                }
                // Reset forms after successful creation
                setNewCategory("");
                setNewSubcategory({name: "", categoryId: ""});
                safeSetCategoryModalOpen(false);
              }}
              disabled={
                modalMode === "category"
                  ? !newCategory.trim() || loading
                  : !newSubcategory.name.trim() ||
                    !newSubcategory.categoryId ||
                    loading
              }
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              {modalMode === "category"
                ? "Crear Categoría"
                : "Crear Subcategoría"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Nuevo Centro de Costos */}
      <Dialog
        open={isCostCenterModalOpen}
        onOpenChange={() => setIsCostCenterModalOpen(false)}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">
              Nuevo Centro de Costos
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="category-select-cost">
                Seleccionar Categoría (Opcional)
              </Label>
              <select
                id="category-select-cost"
                value={newCostCenter.categoryId}
                onChange={(e) =>
                  setNewCostCenter({
                    ...newCostCenter,
                    categoryId: e.target.value
                  })
                }
                className="w-full p-2 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
                disabled={loading}
              >
                <option value="">Selecciona una categoría</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cost-center-name">
                Nombre del Centro de Costos
              </Label>
              <Input
                id="cost-center-name"
                placeholder="Ingresa el nombre del centro de costos"
                value={newCostCenter.name}
                onChange={(e) =>
                  setNewCostCenter({
                    ...newCostCenter,
                    name: e.target.value
                  })
                }
                disabled={loading}
                className="w-full"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={async () => {
                await handleAddCostCenter();
                setIsCostCenterModalOpen(false);
              }}
              disabled={!newCostCenter.name.trim() || loading}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              Crear Centro de Costos
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add the BulkUploadModal for Categories */}
      <BulkUploadModal
        isOpen={isBulkUploadOpen}
        onClose={() => setIsBulkUploadOpen(false)}
        onUpload={async (items) => {
          const result = await handleBulkUpload(items);
          if (result.success) {
            fetchCategories();
          }
        }}
        isUploading={isUploading}
        title="Bulk Upload Categories"
        config={categoriesBulkConfig}
        uploadResult={uploadResult}
      />

      {/* Add the BulkUploadModal for Cost Centers */}
      <BulkUploadModal
        isOpen={isCostCenterBulkUploadOpen}
        onClose={() => setIsCostCenterBulkUploadOpen(false)}
        onUpload={async (items) => {
          const result = await handleCostCenterBulkUpload(items);
          if (result && result.success) {
            fetchCostCenters();
            toast({
              title: "Éxito",
              description: `Centros de costos cargados exitosamente. ${
                result.data?.stats?.created || 0
              } creados, ${result.data?.stats?.updated || 0} actualizados.`,
              variant: "default"
            });
          }
        }}
        isUploading={isCostCenterUploading}
        title="Bulk Upload Cost Centers"
        config={costCentersBulkConfig}
        uploadResult={costCenterUploadResult}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({...deleteDialog, open})}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-red-600">
              Confirmar Eliminación
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="text-center">
              <Trash2 className="h-12 w-12 text-red-500 mx-auto mb-3" />
              <p className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
                ¿Estás seguro de que quieres eliminar "{deleteDialog.name}"?
              </p>

              {deleteDialog.hasSubcategories && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mt-4">
                  <div className="flex items-center gap-2 text-red-700 dark:text-red-300 mb-2">
                    <Folder className="h-4 w-4" />
                    <span className="font-semibold">¡Advertencia!</span>
                  </div>
                  <p className="text-sm text-red-600 dark:text-red-400">
                    Esta categoría tiene{" "}
                    <strong>{deleteDialog.subcategoryCount}</strong>{" "}
                    subcategoría
                    {deleteDialog.subcategoryCount !== 1 ? "s" : ""} que también
                    serán eliminadas.
                  </p>
                  <p className="text-xs text-red-500 dark:text-red-400 mt-2">
                    Esta acción no se puede deshacer.
                  </p>
                </div>
              )}

              <p className="text-sm text-slate-600 dark:text-slate-400 mt-4">
                Esta acción no se puede deshacer.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteDialog({...deleteDialog, open: false})}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                if (deleteDialog.type === "category") {
                  await handleDeleteCategory(deleteDialog.id);
                }
                setDeleteDialog({...deleteDialog, open: false});
              }}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Cost Center Confirmation Dialog */}
      <Dialog
        open={deleteCostCenterDialog.open}
        onOpenChange={(open) =>
          setDeleteCostCenterDialog({...deleteCostCenterDialog, open})
        }
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-red-600">
              Confirmar Eliminación de Centro de Costos
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="text-center">
              <Building2 className="h-12 w-12 text-red-500 mx-auto mb-3" />
              <p className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
                ¿Estás seguro de que quieres eliminar el centro de costos "
                {deleteCostCenterDialog.name}"?
              </p>

              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mt-4">
                <div className="flex items-center gap-2 text-red-700 dark:text-red-300 mb-2">
                  <Trash2 className="h-4 w-4" />
                  <span className="font-semibold">¡Advertencia!</span>
                </div>
                <p className="text-sm text-red-600 dark:text-red-400">
                  Esta acción eliminará permanentemente el centro de costos y no
                  se puede deshacer.
                </p>
              </div>

              <p className="text-sm text-slate-600 dark:text-slate-400 mt-4">
                Esta acción no se puede deshacer.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() =>
                setDeleteCostCenterDialog({
                  ...deleteCostCenterDialog,
                  open: false
                })
              }
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                await handleDeleteCostCenter(deleteCostCenterDialog.id);
                setDeleteCostCenterDialog({
                  ...deleteCostCenterDialog,
                  open: false
                });
              }}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
} 